import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GraphQLResponse, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { Date as DateModel } from '@lsq-frontend/remote-services/all-java/common-mk0';
import {
    ApprovalResponse,
    CreditMemoResponse,
    EarlyPaymentStatus2,
    InvoiceFundingStatus2,
    InvoiceSummary2,
    Query
} from '../../model/models';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isUndefined } from 'lodash';
import { startWith } from 'rxjs/operators';
import {
    mapPayableStatusLegacyToEarlyPmtStatus,
    mapPayableStatusLegacyToInvoiceFundingStatus
} from '../../helpers/status-mapping';
import { GetGroupedInvoiceSummaryForSellerRequest } from '../../model/get-grouped-invoice-summary-for-seller-request';
import { convertDateToLocalDate } from '../../helpers/date-mapping';

export class GetGroupedInvoiceSummaryForSellerResponse2 {
    public ok = false;
    public invoiceSummary = new InvoiceSummary2();
    public error: Error = null;
    public isBuyerPaidEarlyFeePercentageVisible = false;
    public buyerPaidEarlyPaymentFeePercentage = 0;

    public constructor(init?: Partial<GetGroupedInvoiceSummaryForSellerResponse2>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}

@Injectable({ providedIn: 'root' })
export class GetGroupedInvoiceSummaryForSellerService extends ProtoApiService<
    GetGroupedInvoiceSummaryForSellerRequest,
    GetGroupedInvoiceSummaryForSellerResponse2
> {
    public requestType = GetGroupedInvoiceSummaryForSellerRequest;
    public responseType = GetGroupedInvoiceSummaryForSellerResponse2;
    public endpointName = 'GetGroupedInvoiceSummaryForSeller';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'GetGroupedInvoiceSummaryForSeller';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetGroupedInvoiceSummaryForSellerRequest
    ): { query: string } | GetGroupedInvoiceSummaryForSellerRequest {
        if (this._isPayLaterEnabled) {
            const __args = {
                id: +req.refGroupedInvoiceKey,
                companyFilter: { supplierIds: [req.refSellerKey] }
            };
            const amount = { cents: true, currencyCode: true };
            const relationship = {
                buyer: { company: { name: true } },
                scfConfig: {
                    isBuyerPaidEarlyPaymentFeePercentageVisible: true
                }
            };

            return {
                query: `{${jsonToGraphQLQuery(
                    req.isCreditMemo
                        ? ({
                              creditMemo: {
                                  __args,
                                  id: true,
                                  payableName: true,
                                  creditMemoNumber: true,
                                  date: true,
                                  amount,
                                  relationship
                              }
                          } as Query)
                        : ({
                              approval: {
                                  __args,
                                  amount,
                                  payableName: true,
                                  adjustedDueDate: true,
                                  effectiveFundingDate: true,
                                  invoice: {
                                      id: true,
                                      invoiceNumber: true,
                                      date: true,
                                      relationship
                                  }
                              }
                          } as Query)
                )}}`
            };
        }
        return req;
    }

    public responseMapper(
        res:
            | GraphQLResponse<ApprovalResponse | CreditMemoResponse>
            | GetGroupedInvoiceSummaryForSellerResponse2
    ): GetGroupedInvoiceSummaryForSellerResponse2 {
        if (this._isPayLaterEnabled) {
            return !isUndefined((res as any).data.creditMemo)
                ? this._mapToCreditMemoSummary(res as GraphQLResponse<CreditMemoResponse>)
                : this._mapToApprovalSummary(res as GraphQLResponse<ApprovalResponse>);
        }

        return res as GetGroupedInvoiceSummaryForSellerResponse2;
    }

    private _mapToCreditMemoSummary(
        res: GraphQLResponse<CreditMemoResponse>
    ): GetGroupedInvoiceSummaryForSellerResponse2 {
        const creditMemo = (res as GraphQLResponse<CreditMemoResponse>).data.creditMemo;
        const { buyer, scfConfig } = creditMemo.relationship;
        const { isBuyerPaidEarlyPaymentFeePercentageVisible } = scfConfig;

        const invoiceDate = convertDateToLocalDate(creditMemo.date);
        const invoiceDateNew =
            invoiceDate &&
            new DateModel({
                day: invoiceDate.getDate(),
                month: invoiceDate.getMonth() + 1,
                year: invoiceDate.getFullYear()
            });

        return new GetGroupedInvoiceSummaryForSellerResponse2({
            ok: true,
            isBuyerPaidEarlyFeePercentageVisible: isBuyerPaidEarlyPaymentFeePercentageVisible,
            invoiceSummary: new InvoiceSummary2({
                groupId: creditMemo.payableName,
                invoiceDate,
                invoiceDateNew,
                invoiceNumber: creditMemo.creditMemoNumber,
                refGroupedInvoiceKey: creditMemo.id.toString(),
                buyerName: buyer.company.name,
                invoiceStatus: InvoiceFundingStatus2.UNKNOWN_STATUS,
                earlyPaymentStatus: EarlyPaymentStatus2.UNKNOWN_PAYMENT_STATUS,
                finalApprovedAmount: creditMemo.amount
            })
        });
    }

    private _mapToApprovalSummary(
        res: GraphQLResponse<ApprovalResponse>
    ): GetGroupedInvoiceSummaryForSellerResponse2 {
        const { approval } = (res as GraphQLResponse<ApprovalResponse>).data;
        const { invoice } = approval;
        const { buyer, scfConfig } = invoice.relationship;
        const { isBuyerPaidEarlyPaymentFeePercentageVisible } = scfConfig;

        const invoiceDate = convertDateToLocalDate(invoice.date);

        const invoiceDueDate = convertDateToLocalDate(approval.adjustedDueDate);

        const fundingDate = convertDateToLocalDate(approval.effectiveFundingDate);

        return new GetGroupedInvoiceSummaryForSellerResponse2({
            ok: true,
            isBuyerPaidEarlyFeePercentageVisible: isBuyerPaidEarlyPaymentFeePercentageVisible,
            invoiceSummary: new InvoiceSummary2({
                invoiceDate,
                invoiceDueDate,
                fundingDate,
                groupId: approval.payableName,
                refGroupedInvoiceKey: invoice.id,
                invoiceNumber: invoice.invoiceNumber,
                buyerName: buyer.company.name,
                // TODO: feeRateHundredthOfBps,
                invoiceStatus: mapPayableStatusLegacyToInvoiceFundingStatus(approval.statusLegacy),
                earlyPaymentStatus: mapPayableStatusLegacyToEarlyPmtStatus(approval.statusLegacy),
                // TODO: buyerDiscountAmount: payable.buyerDiscountFee,
                // TODO: buyerDiscountPercentage: payable.buyerDiscountFeeRate * 100,
                // TODO: supplierDiscountAmount: payable.supplierDiscountFee,
                finalApprovedAmount: approval.amount
                // TODO: implement these mappings
                // TODO: canBeRevoked, -> add to graphql
                // TODO: buyerPaidTransactionFee, -> add to graphql
                // TODO: groupedInvoiceNumberMatched, -> add to graphql
                // bankAccountType // TODO: add to graphql
            })
        });
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
